import React, { useState, useEffect, Suspense, lazy } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import { Navbar, Footer } from './components/layout';
import { PartyPlatters } from './components/menu';
import { MenuErrorBoundary, FormErrorBoundary } from './components/common';
import { CartProvider } from './context/CartContext';
import BookingFlow from './components/booking/BookingFlow';
import OrderStatus from './components/cart/OrderStatus';

// Lazy-loaded sections and flows
const Contact = lazy(() => import('./components/sections/Contact'));
const OrderFlowManager = lazy(() => import('./components/order/OrderFlowManager'));
const PaymentPage = lazy(() => import('./components/booking/PaymentPage'));
const CheckoutConfirmation = lazy(() => import('./components/cart/CheckoutConfirmation'));
const CartModal = lazy(() => import('./components/cart/CartModal'));
const About = lazy(() => import('./components/sections/About'));

const HomePage = ({ bookingConfig, selectedLocation, onBookingConfigChange }) => {
  const [cartOpen, setCartOpen] = useState(false);
  const [guestSync, setGuestSync] = useState(null);
  const location = useLocation();

  // Scroll to section based on URL (menu or contact only)
  useEffect(() => {
    const scrollToSection = () => {
      const path = location.pathname;
      // Default to menu section for all non-contact routes
      let sectionId = 'menu';

      if (path.includes('/contact')) {
        sectionId = 'contact';
      }
      if (path.includes('/about')) {
        sectionId = 'about';
      }
      
      if (sectionId) {
        // Small delay to ensure the page has rendered
        setTimeout(() => {
          const element = document.getElementById(sectionId);
          if (element) {
            // Calculate offset for mobile navbar height
            const isMobile = window.innerWidth < 960; // md breakpoint
            const offset = isMobile ? 92 : 48; // Mobile navbar is taller
            
            const elementPosition = element.getBoundingClientRect().top;
            const offsetPosition = elementPosition + window.pageYOffset - offset;
            
            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            });
          }
        }, 200); // Increased delay for mobile
      }
    };

    scrollToSection();
  }, [location.pathname]);

  return (
    <>
      <Navbar selectedLocation={selectedLocation} />
      <Box component="main" sx={{ paddingTop: { xs: '92px', md: '48px' } }}>
        <MenuErrorBoundary>
          <PartyPlatters 
            id="menu" 
            bookingConfig={bookingConfig} 
            onBookingConfigChange={onBookingConfigChange}
            onOpenCart={() => setCartOpen(true)}
            onGuestSyncChange={setGuestSync}
          />
        </MenuErrorBoundary>
        <Suspense fallback={null}>
          <About />
        </Suspense>
        <FormErrorBoundary>
          <Suspense fallback={null}>
            <Box id="contact">
              <Contact />
            </Box>
          </Suspense>
        </FormErrorBoundary>
      </Box>
      <Footer />
      
      {/* Order Flow Manager for Modal Flow */}
      <Suspense fallback={null}>
        <OrderFlowManager 
          cartOpen={cartOpen} 
          onCartClose={() => setCartOpen(false)}
          bookingConfig={bookingConfig}
          onBookingConfigChange={onBookingConfigChange}
          guestCount={guestSync?.guestCount}
          onGuestCountChange={guestSync?.onGuestCountChange}
          selectedMenu={guestSync?.selectedMenu}
          userEditRef={guestSync?.userEditRef}
          lastUserEditAt={guestSync?.lastUserEditAt}
        />
      </Suspense>
    </>
  );
};

const App = () => {
  // Initialize bookingConfig from localStorage if available
  const [bookingConfig, setBookingConfig] = useState(() => {
    const saved = localStorage.getItem('biteAffairs_bookingConfig');
    const config = saved ? JSON.parse(saved) : null;
    return config;
  });
  const [selectedLocation, setSelectedLocation] = useState(null);

  const navigate = useNavigate();

  // Sync bookingConfig changes with localStorage
  useEffect(() => {
    if (bookingConfig) {
      localStorage.setItem('biteAffairs_bookingConfig', JSON.stringify(bookingConfig));
    }
  }, [bookingConfig]);

  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
  };

  const handleBookingComplete = (config) => {
    setBookingConfig(config);
    // Save to localStorage for persistence across page refreshes
    localStorage.setItem('biteAffairs_bookingConfig', JSON.stringify(config));
    navigate('/menu');
  };

  const handleBookingConfigChange = (updates) => {
    setBookingConfig((currentConfig) => ({ ...currentConfig, ...updates }));
  };

  const clearBookingConfig = () => {
    setBookingConfig(null);
    localStorage.removeItem('biteAffairs_bookingConfig');
  };

  return (
    <CartProvider>
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', width: '100%', overflowX: 'hidden' }}>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<BookingFlow onComplete={handleBookingComplete} onLocationSelect={handleLocationSelect} />} />
          <Route path="/menu" element={<HomePage bookingConfig={bookingConfig} selectedLocation={selectedLocation} onBookingConfigChange={handleBookingConfigChange} />} />
          <Route path="/about" element={<HomePage bookingConfig={bookingConfig} selectedLocation={selectedLocation} onBookingConfigChange={handleBookingConfigChange} />} />
          <Route path="/contact" element={<HomePage bookingConfig={bookingConfig} selectedLocation={selectedLocation} onBookingConfigChange={handleBookingConfigChange} />} />
          <Route path="/cart" element={<Suspense fallback={null}><CartModal open={true} /></Suspense>} />
          <Route path="/checkout" element={<Suspense fallback={null}><CheckoutConfirmation open={true} /></Suspense>} />
          <Route path="/order-status/:orderId" element={<OrderStatus />} />
          <Route path="/payment" element={<Suspense fallback={null}><PaymentPage /></Suspense>} />
        </Routes>
      </Box>
    </CartProvider>
  );
};

export default App;
